import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { api, setAuthToken, clearAuthToken } from '@/services/api'

interface User {
  id: string
  email: string
  email_verified: boolean
  user_type: 'expert' | 'company'
  status: string
  created_at: string
  updated_at: string
  last_login_at?: string
}

interface AuthResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
  user: User
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  userType: 'expert' | 'company' | null
  userId: string | null
  login: (email: string, userType: 'expert' | 'company') => Promise<void>
  verifyMagicLink: (token: string, userType: 'expert' | 'company') => Promise<void>
  loginWithOAuth: (provider: 'google' | 'github' | 'linkedin', userType: 'expert' | 'company') => void
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_KEY = 'user'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY)
      const storedUser = localStorage.getItem(USER_KEY)

      if (storedToken && storedUser) {
        setAuthToken(storedToken)
        setUser(JSON.parse(storedUser))

        // Validate token by fetching current profile
        try {
          await api.get('/api/v1/profiles/me')
        } catch {
          // Token invalid, try to refresh
          try {
            await refreshTokenInternal()
          } catch {
            clearAuth()
          }
        }
      }

      setIsLoading(false)
    }

    initAuth()
  }, [])

  const clearAuth = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    clearAuthToken()
    setUser(null)
  }

  const saveAuth = (response: AuthResponse) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.access_token)
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)
    localStorage.setItem(USER_KEY, JSON.stringify(response.user))
    setAuthToken(response.access_token)
    setUser(response.user)
  }

  const refreshTokenInternal = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      throw new Error('No refresh token')
    }

    const response = await api.post<AuthResponse>('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    })

    saveAuth(response.data)
  }

  // Request magic link
  const login = async (email: string, userType: 'expert' | 'company') => {
    await api.post('/api/v1/auth/magic-link', {
      email,
      user_type: userType,
    })
  }

  // Verify magic link token
  const verifyMagicLink = async (token: string, userType: 'expert' | 'company') => {
    const response = await api.post<AuthResponse>(`/api/v1/auth/verify?type=${userType}`, {
      token,
    })
    saveAuth(response.data)
  }

  // Start OAuth flow
  const loginWithOAuth = (provider: 'google' | 'github' | 'linkedin', userType: 'expert' | 'company') => {
    const redirectUri = `${window.location.origin}/auth/callback`
    window.location.href = `/api/v1/auth/oauth/${provider}?type=${userType}&redirect_uri=${encodeURIComponent(redirectUri)}`
  }

  // Logout
  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (refreshToken) {
      try {
        await api.post('/api/v1/auth/logout', { refresh_token: refreshToken })
      } catch {
        // Ignore errors on logout
      }
    }
    clearAuth()
  }, [])

  // Refresh token
  const refreshToken = useCallback(async () => {
    await refreshTokenInternal()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        userType: user?.user_type ?? null,
        userId: user?.id ?? null,
        login,
        verifyMagicLink,
        loginWithOAuth,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
